import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'
import { CommissionDefault as CommissionDefaultModel } from './core/CommissionDefault.model'
import { configDefaultCommission, updateDefaultCommissionConfig } from './ apis/requestsApi'

// ===== Helpers =====
const parseNum = (raw: string) => parseFloat((raw ?? '').replace(',', '.'))
const to4 = (n: number) => (Number.isFinite(n) ? Number(parseFloat(n.toFixed(4))) : n)
const fmt = (n: number | string) => {
  if (n === null || n === undefined || n === '') return ''
  const v = typeof n === 'string' ? parseNum(n) : n
  if (!Number.isFinite(v)) return ''
  return parseFloat(v.toFixed(4)).toString().replace('.', ',')
}
const validateValue = (value: number, isPercentage: boolean) =>
  Number.isFinite(value) && value >= 0 && (isPercentage ? value <= 100 : true)

type RowUI = {
  techPartner: string
  comissionBaseTech: string
  comissionBaseCommercial: string
  isPercentageTech: boolean
  isPercentageCommercial: boolean
}

const DEFAULT_PARTNERS = ['Passport', 'Conekta', 'Enefevo']

export const CommissionDefault: React.FC = () => {
  const intl = useIntl()
  const [rows, setRows] = useState<RowUI[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const commissions = await configDefaultCommission()
        if (!commissions || commissions.length === 0) {
          setRows(
            DEFAULT_PARTNERS.map(tp => ({
              techPartner: tp,
              comissionBaseTech: '',
              comissionBaseCommercial: '',
              isPercentageTech: true,
              isPercentageCommercial: true,
            }))
          )
        } else {
          setRows(
            commissions.map((c: any) => ({
              techPartner: c.techPartner,
              comissionBaseTech: fmt(c.comissionBaseTech ?? c.pctBaseTech ?? ''),
              comissionBaseCommercial: fmt(c.comissionBaseCommercial ?? c.pctBaseCommercial ?? ''),
              isPercentageTech: typeof c.isPercentageTech === 'boolean' ? c.isPercentageTech : true,
              isPercentageCommercial:
                typeof c.isPercentageCommercial === 'boolean' ? c.isPercentageCommercial : true,
            }))
          )
        }
      } catch {
        Swal.fire('Error', 'No se pudieron cargar las comisiones', 'error')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const revalidateCell = (idx: number, field: 'comissionBaseTech' | 'comissionBaseCommercial', raw: string) => {
    const row = rows[idx]
    const isPct = field === 'comissionBaseTech' ? row.isPercentageTech : row.isPercentageCommercial
    const numeric = parseNum(raw)
    const key = `${idx}-${field}`
    if (!validateValue(numeric, isPct) && raw !== '') {
      setErrors(e => ({
        ...e,
        [key]: isPct ? '0–100, hasta 4 decimales' : '≥ 0, hasta 4 decimales',
      }))
    } else {
      setErrors(e => {
        const { [key]: _, ...rest } = e
        return rest
      })
    }
  }

  const onChange = (idx: number, field: 'comissionBaseTech' | 'comissionBaseCommercial', raw: string) => {
    setRows(prev => {
      const copy = [...prev]
      copy[idx]![field] = raw
      return copy
    })
    revalidateCell(idx, field, raw)
  }

  const toggleType = (idx: number, field: 'isPercentageTech' | 'isPercentageCommercial', value: boolean) => {
    setRows(prev => {
      const copy = [...prev]
      copy[idx]![field] = value
      return copy
    })
  }

  const canSave = useMemo(() => {
    if (loading || saving) return false
    if (Object.keys(errors).length) return false
    return rows.every(r =>
      Number.isFinite(parseNum(r.comissionBaseTech)) &&
      Number.isFinite(parseNum(r.comissionBaseCommercial))
    )
  }, [loading, saving, errors, rows])

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    Swal.fire({ title: 'Guardando…', didOpen: () => Swal.showLoading(), allowOutsideClick: false })
    try {
      const payload: CommissionDefaultModel[] = rows.map(r => ({
        techPartner: r.techPartner,
        comissionBaseTech: to4(parseNum(r.comissionBaseTech)),
        isPercentageTech: r.isPercentageTech,
        comissionBaseCommercial: to4(parseNum(r.comissionBaseCommercial)),
        isPercentageCommercial: r.isPercentageCommercial,
      }))
      await updateDefaultCommissionConfig(payload)
      Swal.close()
      Swal.fire('¡Listo!', 'Comisiones actualizadas por socio', 'success')
    } catch {
      Swal.close()
      Swal.fire('Error', 'Falló al guardar las comisiones', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p>Cargando comisiones…</p>

  const activeBtnStyle = {
    backgroundColor: 'var(--kt-primary)',
    color: '#fff',
    borderColor: 'var(--kt-primary)',
  }

  return (
    <div className="card p-4">
      <h3>Comisión por Socio Tecnológico</h3>
      <p className="text-muted mb-4">
        Ahora puedes definir si cada socio usa porcentaje (%) o monto fijo ($) en cada tipo de comisión.
      </p>

      <div className="table-responsive mb-4">
        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th>Socio Tecnológico</th>
              <th>Comisión Socio Tecnológico</th>
              <th>Comisión Socio Comercial</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.techPartner}>
                <td>{r.techPartner}</td>

                {/* Tech */}
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <div className="btn-group me-2">
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={r.isPercentageTech ? activeBtnStyle : { borderColor: 'var(--kt-primary)', color: 'var(--kt-primary)' }}
                        onClick={() => toggleType(idx, 'isPercentageTech', true)}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={!r.isPercentageTech ? activeBtnStyle : { borderColor: 'var(--kt-primary)', color: 'var(--kt-primary)' }}
                        onClick={() => toggleType(idx, 'isPercentageTech', false)}
                      >
                        $
                      </button>
                    </div>

                    {!r.isPercentageTech && <span>$</span>}
                    <input
                      type="text"
                      className={`form-control ${errors[`${idx}-comissionBaseTech`] ? 'is-invalid' : ''}`}
                      placeholder={r.isPercentageTech ? '0 – 100' : '≥ 0.0000'}
                      value={r.comissionBaseTech}
                      onChange={e => onChange(idx, 'comissionBaseTech', e.target.value)}
                    />
                    {r.isPercentageTech && <span>%</span>}
                  </div>
                </td>

                {/* Commercial */}
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <div className="btn-group me-2">
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={r.isPercentageCommercial ? activeBtnStyle : { borderColor: 'var(--kt-primary)', color: 'var(--kt-primary)' }}
                        onClick={() => toggleType(idx, 'isPercentageCommercial', true)}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={!r.isPercentageCommercial ? activeBtnStyle : { borderColor: 'var(--kt-primary)', color: 'var(--kt-primary)' }}
                        onClick={() => toggleType(idx, 'isPercentageCommercial', false)}
                      >
                        $
                      </button>
                    </div>

                    {!r.isPercentageCommercial && <span>$</span>}
                    <input
                      type="text"
                      className={`form-control ${errors[`${idx}-comissionBaseCommercial`] ? 'is-invalid' : ''}`}
                      placeholder={r.isPercentageCommercial ? '0 – 100' : '≥ 0.0000'}
                      value={r.comissionBaseCommercial}
                      onChange={e => onChange(idx, 'comissionBaseCommercial', e.target.value)}
                    />
                    {r.isPercentageCommercial && <span>%</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-end">
        <button className="btn btn-primary" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}
